import { MatchScore } from './methodology/types';
import {
  CREDIT_SCORE_FULL_POINTS,
  ITEMIZED_CREDIT_SCORE,
  ITEMIZED_METHODOLOGY,
  ITEMIZED_PRIMARY_GOAL,
  METHODOLOGY_FULL_POINTS,
  Methodology,
  PRIMARY_GOAL_FULL_POINTS,
  EXCLUDED_OFFER_ID_SET,
} from './constants';
import { CreditScore, PrimaryGoal } from '../../types/inputs';
import {
  CardsMarketplaceOffer,
  CardsMarketplaceOfferCompareCategories,
  QuizOffer,
  QuizResponse,
} from '../../types/marketplace';
import getBigPurchaseMethodology from './methodology/big-purchase';
import {
  getBuildCreditMethodology,
  getStudentMethodology,
} from './methodology/build-credit';
import getCashBackMethodology from './methodology/cash-back';
import getDebtMethodology from './methodology/debt';
import getTravelMethodology from './methodology/travel';
import { ItemizedScore } from './methodology/types';
import { ExperimentsReducer } from 'src/redux/reducers/experiments';
import {
  isNewBalanceTransferFlowExperimentActive,
  isNewBigPurchaseFlowExperimentActive,
  isNewBuildCreditFlowExperimentActive,
} from '../experiments';
import getBigPurchaseVariantMethodology from './methodology/big-purchase_variant';
import { InputsReducer } from 'src/redux/reducers/inputs';
import { CREDIT_SCORE_BANDS } from 'src/lib/credit-utils';
import getStudentVariantScore from './methodology/student-variant';
import getDebtVariantScore from './methodology/debt-variant';
import { CardsMarketplaceOfferAO } from '../approval-odds/types';

export type QueryField = {
  field: string;
  type: string;
  value: string | boolean | string[] | number;
};

const getItemizedScore = (
  creditScorePoints: number,
  methodologyPoints: number
): ItemizedScore[] => [
  {
    awardedPoints: PRIMARY_GOAL_FULL_POINTS,
    availablePoints: PRIMARY_GOAL_FULL_POINTS,
    name: ITEMIZED_PRIMARY_GOAL,
  },
  {
    awardedPoints: creditScorePoints,
    availablePoints: CREDIT_SCORE_FULL_POINTS,
    name: ITEMIZED_CREDIT_SCORE,
  },
  {
    awardedPoints: methodologyPoints,
    availablePoints: METHODOLOGY_FULL_POINTS,
    name: ITEMIZED_METHODOLOGY,
  },
];

export const getSortedOffers = (offers: QuizOffer[]): QuizOffer[] =>
  offers.sort((a, b) => {
    if (a.matchScore !== b.matchScore) {
      return b.matchScore - a.matchScore;
    }
    return (
      (b.offer.details.rating_stars ?? 0) - (a.offer.details.rating_stars ?? 0)
    );
  });

const CREDIT_BANDS_ORDER = {
  [CreditScore.LimitedCreditHistory]: 0, //TODO - it should be defined here https://nerdwallet.atlassian.net/browse/CCIN-1340
  [CreditScore.Poor]: 0,
  [CreditScore.Average]: 1,
  [CreditScore.Good]: 2,
  [CreditScore.Excellent]: 3,
};
const CREDIT_BANDS_LIST = [
  CreditScore.LimitedCreditHistory,
  CreditScore.Poor,
  CreditScore.Average,
  CreditScore.Good,
  CreditScore.Excellent,
];

export const getCreditScorePointsForOffer = (
  userCsBand: CreditScore,
  offer: CardsMarketplaceOffer,
  experiments: ExperimentsReducer
): number => {
  if (
    userCsBand === CreditScore.LimitedCreditHistory &&
    offer.details.limited_credit_history &&
    isNewBuildCreditFlowExperimentActive(experiments)
  )
    return CREDIT_SCORE_FULL_POINTS;

  const { min_fico: minFico, max_fico: maxFico } = offer.details;
  const offerMinBand =
    CREDIT_BANDS_LIST.find((band) => CREDIT_SCORE_BANDS[band][0] === minFico) ??
    CreditScore.Poor;
  const offerMaxBand =
    CREDIT_BANDS_LIST.find((band) => CREDIT_SCORE_BANDS[band][1] === maxFico) ??
    CreditScore.Excellent;

  if (userCsBand === offerMaxBand) return CREDIT_SCORE_FULL_POINTS;
  if (
    CREDIT_BANDS_ORDER[userCsBand] >= CREDIT_BANDS_ORDER[offerMinBand] &&
    CREDIT_BANDS_ORDER[userCsBand] < CREDIT_BANDS_ORDER[offerMaxBand]
  )
    return 0.8 * CREDIT_SCORE_FULL_POINTS;
  return 0;
};

export type CalculateMethodology = (
  answers: InputsReducer,
  offer: CardsMarketplaceOffer,
  experiments?: ExperimentsReducer
) => MatchScore;
export const METHODOLOGY_FUNCTIONS: Record<Methodology, CalculateMethodology> =
  {
    [Methodology.Travel]: getTravelMethodology,
    [Methodology.BigPurchase]: getBigPurchaseMethodology,
    [Methodology.BigPurchaseVariant]: getBigPurchaseVariantMethodology,
    [Methodology.BuildCredit]: getBuildCreditMethodology,
    [Methodology.CashBack]: getCashBackMethodology,
    [Methodology.Debt]: getDebtMethodology,
    [Methodology.DebtVariant]: getDebtVariantScore,
    [Methodology.Student]: getStudentMethodology,
    [Methodology.StudentVariant]: getStudentVariantScore,
  };

export const getMethodology = (
  inputs: InputsReducer,
  experiments: ExperimentsReducer
): Methodology => {
  switch (inputs.primaryGoal) {
    case PrimaryGoal.BigPurchase:
      if (isNewBigPurchaseFlowExperimentActive(experiments)) {
        return Methodology.BigPurchaseVariant;
      }
      return Methodology.BigPurchase;
    case PrimaryGoal.Travel:
      return Methodology.Travel;
    case PrimaryGoal.BuildCredit:
      if (inputs.collegeStudent) {
        if (isNewBuildCreditFlowExperimentActive(experiments)) {
          return Methodology.StudentVariant;
        }
        return Methodology.Student;
      }
      return Methodology.BuildCredit;
    case PrimaryGoal.CashBack:
      return Methodology.CashBack;
    case PrimaryGoal.Debt:
      if (isNewBalanceTransferFlowExperimentActive(experiments)) {
        return Methodology.DebtVariant;
      }
      return Methodology.Debt;
    default:
      return Methodology.Debt;
  }
};

type MethodologyScore = {
  score: number;
  itemizedScore: ItemizedScore[];
};
export const getMethodologyScore = (
  methodology: Methodology,
  inputs: InputsReducer,
  offer: CardsMarketplaceOffer,
  experiments: ExperimentsReducer
): MethodologyScore => {
  const {
    score: { awardedPoints, availablePoints },
    itemizedScore,
  } = METHODOLOGY_FUNCTIONS[methodology](
    inputs,
    offer,
    experiments
  ) as MatchScore;

  return {
    score: (awardedPoints / availablePoints) * METHODOLOGY_FULL_POINTS,
    itemizedScore,
  };
};

export const transformOffers = (
  offers: CardsMarketplaceOffer[] | [],
  inputs: InputsReducer,
  limit: number | null,
  experiments: ExperimentsReducer
): QuizResponse => {
  if (!inputs.creditScore) return { offers: [] };

  const methodology = getMethodology(inputs, experiments);

  const mappedOffers = offers.map((offer) => {
    const creditScorePoints = getCreditScorePointsForOffer(
      inputs.creditScore as CreditScore,
      offer,
      experiments
    );
    const { score, itemizedScore } = getMethodologyScore(
      methodology,
      inputs,
      offer,
      experiments
    );
    return {
      offer,
      matchScore: PRIMARY_GOAL_FULL_POINTS + creditScorePoints + score,
      itemizedScore: getItemizedScore(creditScorePoints, score),
      itemizedMethodology: itemizedScore,
    } as QuizOffer;
  });

  const sortedAndLimitedOffers = getSortedOffers(mappedOffers).slice(
    0,
    limit ?? 5
  );

  return {
    offers: sortedAndLimitedOffers,
  };
};

export const commonQuery = [
  {
    field: 'offer.details.compare_categories',
    type: 'contains_none_of',
    value: ['shadow-cards', 'business-credit-cards'],
  },
  {
    field: 'offer.status',
    type: 'eq',
    value: 'ACTIVE',
  },
  {
    field: 'offer.details.is_monetizing',
    type: 'eq',
    value: true,
  },
];

const QUERY_VALUES: Record<PrimaryGoal, (collegeStudent: boolean) => string[]> =
  {
    [PrimaryGoal.BigPurchase]: () => ['low-interest-credit-cards'],
    [PrimaryGoal.BuildCredit]: (collegeStudent: boolean) => [
      collegeStudent ? 'student-credit-cards' : 'bad-credit-cards',
    ],
    [PrimaryGoal.CashBack]: () => ['cash-rewards-credit-cards'],
    [PrimaryGoal.Debt]: () => ['balance-transfer-credit-cards'],
    [PrimaryGoal.Travel]: () => ['travel-miles-credit-cards'],
  };

export const getQuery = (inputs: InputsReducer): QueryField[] => {
  const { primaryGoal, collegeStudent, annualFees } = inputs;

  const primaryGoalQueryValue = QUERY_VALUES[primaryGoal as PrimaryGoal](
    collegeStudent ?? false
  );

  return [
    ...commonQuery,
    {
      field: 'offer.details.compare_categories',
      type: 'contains_all',
      value: primaryGoalQueryValue,
    },
    ...(!collegeStudent
      ? [
          {
            field: 'offer.details.compare_categories',
            type: 'contains_none_of',
            value: ['student-credit-cards'],
          },
        ]
      : []),
    ...(!annualFees && isNewBuildCreditFlowExperimentActive({})
      ? [{ field: 'offer.details.annual_fee', type: 'eq', value: 0 }]
      : []),
  ];
};

export const excludeInstitutions = (
  query: QueryField[],
  excludedInstitutions: string[] | undefined
) => {
  if (excludedInstitutions && excludedInstitutions?.length) {
    query.push({
      field: 'offer.product.institution.id',
      type: 'not_in',
      value: excludedInstitutions as string[],
    });
  }
  return query;
};

export const modifyCompareCategories = (
  offers: CardsMarketplaceOfferAO[] | []
) => {
  return offers.map((offer) => {
    const compare_categories = offer.details.compare_categories || [];
    if (EXCLUDED_OFFER_ID_SET.has(offer.id)) {
      compare_categories.push(
        CardsMarketplaceOfferCompareCategories.CardfinderExcluded
      );
    }
    return { ...offer, details: { ...offer.details, compare_categories } };
  });
};
